import { useFormikContext } from 'formik'
import React, { useMemo, useContext } from 'react'

import { PaymentMethodStatus } from '@enums'
import { ConfirmPaymentParams, SubmitEventPayload } from '@hooks/useBookingFlow'
import useNavigate from '@hooks/useNavigate'
import date from '@lib/date'
import paramUtils from '@lib/params'
import url from '@lib/url'
import { PaymentMethod } from '@pages/Checkout/hooks/Payment/useBookingPayment'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useFinalPrice } from '@pages/Checkout/hooks/usePrice'
import PaymentLabel from '@pages/Checkout/Payment/Label'
import { PluginContext } from '@plugins/index'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'
import { ParamsStore, useParams } from '@stores/params'

const buildRedirectionLink = (params: ParamsStore, id?: number): string =>
  url.build(['/', 'reservation', 'actionRequired', `${id}`], { ...paramUtils.getPersistentUrlParams(params) })

const isPriceValidForOXXO = (
  visiblePrice: { currency: string; fractional: number } | null,
  currency: string | null,
  minPriceThreshold: number,
  maxPriceThreshold?: number | null,
): boolean => {
  const OXXO_CURRENCY = 'MXN'

  if (currency !== OXXO_CURRENCY || !visiblePrice || visiblePrice.currency !== OXXO_CURRENCY) {
    return false
  }

  const meetsMinThreshold = visiblePrice.fractional >= minPriceThreshold * 100
  if (!meetsMinThreshold) {
    return false
  }

  if (maxPriceThreshold) {
    const meetsMaxThreshold = visiblePrice.fractional <= maxPriceThreshold * 100
    return meetsMaxThreshold
  }

  return true
}

interface Props {
  provider: PaymentProvider
}

const DEFAULT_MIN_THRESHOLD = 10
const DEFAULT_MIN_THRESHOLD_HOURS = 5

export const useOxxo = ({ provider }: Props): PaymentMethod => {
  const { navigate } = useNavigate()
  const [params] = useParams()
  const [{ outbound }] = useCheckout()
  const [{ paymentProvidersMethods }] = useSettings()
  const { values } = useFormikContext<CheckoutFormData>()
  const {
    hooks: { useVisiblePrice = useFinalPrice },
  } = useContext(PluginContext)

  const minPriceThreshold = paymentProvidersMethods.ox?.options?.minPriceThreshold ?? DEFAULT_MIN_THRESHOLD
  const maxPriceThreshold = paymentProvidersMethods.ox?.options?.maxPriceThreshold
  const minHoursToDeparture = paymentProvidersMethods.ox?.options?.minHoursToDeparture ?? DEFAULT_MIN_THRESHOLD_HOURS
  const visiblePrice = useVisiblePrice()
  const meetsPriceThreshold = isPriceValidForOXXO(visiblePrice, params.currency, minPriceThreshold, maxPriceThreshold)

  const meetsTimeThreshold = useMemo(() => {
    const rawDeparture = outbound?.departureTime ?? values.reservationInfo?.departureTime

    if (!rawDeparture) return false
    const departure = date.parse(rawDeparture, 'UTC')
    const msToDeparture = departure.getTime() - Date.now()
    return msToDeparture >= minHoursToDeparture * 60 * 60 * 1000
  }, [outbound?.departureTime, values.reservationInfo?.departureTime, minHoursToDeparture])

  const status = meetsPriceThreshold && meetsTimeThreshold ? PaymentMethodStatus.Ready : PaymentMethodStatus.Rejected

  return useMemo(
    () => ({
      status,
      getOption: () => ({
        value: 'ox',
        label: <PaymentLabel type="ox" />,
      }),
      provider,
      on: {
        submitForm: (): SubmitEventPayload => ({ paymentMethodData: { type: 'ox' } }),
        confirmPayment: async (data: ConfirmPaymentParams): Promise<void> => {
          if (data.action === 'ox') navigate(buildRedirectionLink(params, values.reservationData?.id))
        },
      },
    }),
    [navigate, params, provider, status, values.reservationData?.id],
  )
}
